/**
 * Slim "list rows" for the portals: clientListRows/{clientId} holds only the
 * client fields the client lists, badges and pickers read, in the same nested
 * shape as the client doc. Full client docs for every live client came to
 * ~27 MB (Oct 2026, mostly recommendations / fundProjections / amendments);
 * the rows are ~1.3 MB.
 *
 * The server keeps the rows in step with clients/{id} (server/lib/
 * clientListRowsMirror.cjs); the browser only reads them
 * (src/services/liveClientsStore.js). A row exists only for a live client
 * (listBucket == 'live', see clientListBucket.js).
 *
 * A screen that needs a field not listed here must add it to
 * CLIENT_LIST_ROW_FIELDS (the server rewrites every row on its next start) or
 * read the full client doc. Opening a client always loads the full doc.
 *
 * CommonJS body so Node 18 can require() it and webpack can bundle it
 * (see src/utils/clientListBucket.js). Keep it free of object rest/spread.
 */

const CLIENT_LIST_ROWS_COLLECTION = 'clientListRows';

/** Dot paths copied from the client doc. A path ending at a map copies the whole map. */
const CLIENT_LIST_ROW_FIELDS = Object.freeze([
  'createdAt',
  'createdBy',
  'createdByName',
  'listBucket',
  'name',
  'personal.firstName',
  'personal.middleName',
  'personal.surname',
  'personal.lastName',
  'personal.preferredName',
  'personal.fullName',
  'personal.email',
  'personal.contactNumber',
  'assignment',
  'importSource.kind',
  'importSource.importedAt',
  'csvMerge.releasedAt',
  'fileNotesMerge',
  'isLost',
  'isTestClient',
  'isArchived',
  'isDnq',
  'lastARCompletedDate',
  'workflowStatus',
]);

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function readPath(source, parts) {
  let value = source;
  for (let i = 0; i < parts.length; i += 1) {
    if (!isPlainObject(value)) return undefined;
    value = value[parts[i]];
  }
  return value;
}

function writePath(target, parts, value) {
  let node = target;
  for (let i = 0; i < parts.length - 1; i += 1) {
    if (!isPlainObject(node[parts[i]])) node[parts[i]] = {};
    node = node[parts[i]];
  }
  node[parts[parts.length - 1]] = value;
}

/**
 * The list row for a client doc's data (no `id`; the row's doc id is the client id).
 * Values are copied by reference, so Firestore Timestamps stay Timestamps.
 */
function buildClientListRow(client) {
  const row = {};
  if (!isPlainObject(client)) return row;
  CLIENT_LIST_ROW_FIELDS.forEach((path) => {
    const parts = path.split('.');
    const value = readPath(client, parts);
    if (value !== undefined) writePath(row, parts, value);
  });
  return row;
}

function normaliseForKey(value) {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'object') return value;
  // Firestore Timestamp (Admin SDK and web SDK both have toMillis).
  if (typeof value.toMillis === 'function') return { $ts: value.toMillis() };
  if (value instanceof Date) return { $ts: value.getTime() };
  // DocumentReference / GeoPoint: compare by their path or coordinates.
  if (typeof value.path === 'string' && value.firestore) return { $ref: value.path };
  if (typeof value.latitude === 'number' && typeof value.longitude === 'number') {
    return { $geo: [value.latitude, value.longitude] };
  }
  if (Array.isArray(value)) return value.map(normaliseForKey);
  const out = {};
  Object.keys(value).sort().forEach((key) => {
    if (value[key] !== undefined) out[key] = normaliseForKey(value[key]);
  });
  return out;
}

const LIST_ROW_MARK = '__clientListRow';

/**
 * Mark a delivered row object (browser). Non-enumerable, so it is never
 * written back to Firestore and a spread copy no longer counts as a row.
 */
function markClientListRow(client) {
  if (client && typeof client === 'object') Object.defineProperty(client, LIST_ROW_MARK, { value: true });
  return client;
}

/** True for a list row: only the list fields, never a stand-in for the client doc. */
function isClientListRow(client) {
  return Boolean(client && client[LIST_ROW_MARK]);
}

/**
 * The open client's full doc for a page that reads or saves client data:
 * `clientData`, else `selectedClient` unless that is still the list row the
 * client was picked from (null until getClientRecord answers).
 */
function fullClientRecord(clientData, selectedClient) {
  if (clientData) return clientData;
  return selectedClient && !isClientListRow(selectedClient) ? selectedClient : null;
}

/** Stable comparison key for a row: equal keys mean the stored row needs no rewrite. */
function clientListRowKey(row) {
  return JSON.stringify(normaliseForKey(row || {}));
}

module.exports = {
  CLIENT_LIST_ROWS_COLLECTION,
  CLIENT_LIST_ROW_FIELDS,
  buildClientListRow,
  clientListRowKey,
  markClientListRow,
  isClientListRow,
  fullClientRecord,
};
